// Säntisbatterie – Batteriefinder cascade (custom element <batteriefinder-widget>).
// Shared by sections/batteriefinder-widget.liquid (horizontal, Maranello "filters"
// look) and sections/batteriefinder-vertikal.liquid ("vertical-filters" look).
// Cascade widget: each <select> is filled from the finder endpoint
// (no params → types, ?type= → brands, +&brand= → models).
//
// Preselection (2026-10-20): the widget restores ?type=&brand=&model= from the
// page URL, so on the finder page itself it shows the current selection.
// Submit: when the finder section (sections/produktfinder.liquid, element
// `.mw-pf`) is on the same page, the URL is updated in place and the section
// re-renders the recommendations (event `mw:finder-navigate`, no reload);
// otherwise it navigates to the finder page with the chosen labels as query
// params — the page restores that state itself.
if (!customElements.get('batteriefinder-widget')) {
  class BatteriefinderWidget extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('button[type="submit"]');
      this.selects = Array.prototype.slice.call(this.querySelectorAll('select.dropdown-filter'));
      this.source = this.dataset.source;
      this.target = this.dataset.target;

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.go();
      });
      this.selects.forEach((select, index) => {
        select.addEventListener('change', () => this.onChange(index));
      });
      // Browser back/forward on the finder page: follow the URL.
      this.onPopState = () => this.preselect();
      window.addEventListener('popstate', this.onPopState);

      this.load({}, 'types', 0).then(() => this.preselect());
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
    }

    query(params) {
      const qs = new URLSearchParams(params).toString();
      return fetch(this.source + (qs ? '?' + qs : ''), {
        headers: { Accept: 'application/json' },
      }).then((response) => {
        if (!response.ok) throw new Error('finder ' + response.status);
        return response.json();
      });
    }

    // Drop every option but the placeholder and disable the select.
    clear(fromIndex) {
      this.selects.forEach((select, index) => {
        if (index < fromIndex) return;
        select.querySelectorAll('option:not(.default)').forEach((option) => option.remove());
        select.querySelector('option.default').selected = true;
        select.disabled = true;
        select.closest('.form-group').classList.add('disabled');
      });
    }

    fill(index, labels) {
      const select = this.selects[index];
      labels.forEach((label) => {
        const option = document.createElement('option');
        option.value = label;
        option.textContent = label;
        select.appendChild(option);
      });
      if (labels.length) {
        select.disabled = false;
        select.closest('.form-group').classList.remove('disabled');
      }
    }

    load(params, key, index) {
      this.toggleLoading(true);
      return this.query(params)
        .then((json) => this.fill(index, json[key] || []))
        .catch((error) => console.warn('Batteriefinder-Widget:', error))
        .then(() => this.toggleLoading(false));
    }

    // Select the option whose value equals *label*; false when it is unknown.
    pick(index, label) {
      const select = this.selects[index];
      const option = Array.prototype.find.call(select.options, (o) => o.value === label);
      if (!option) return false;
      select.value = label;
      return true;
    }

    // Restore ?type=&brand=&model= from the page URL (types must be loaded).
    preselect() {
      const params = new URLSearchParams(window.location.search);
      const type = params.get('type');
      const brand = params.get('brand');
      const model = params.get('model');
      this.selects[0].querySelector('option.default').selected = true;
      this.clear(1);
      if (!type || !this.pick(0, type)) {
        this.button.disabled = true;
        return Promise.resolve();
      }
      this.button.disabled = false;
      return this.load({ type: type }, 'brands', 1).then(() => {
        if (!brand || !this.pick(1, brand)) return;
        return this.load({ type: type, brand: brand }, 'models', 2).then(() => {
          if (model) this.pick(2, model);
        });
      });
    }

    onChange(index) {
      this.clear(index + 1);
      const type = this.selects[0].value;
      const brand = this.selects[1].value;
      // Partial selections are fine — the finder page continues from there.
      this.button.disabled = !type;
      if (index === 0 && type) {
        this.load({ type: type }, 'brands', 1);
      } else if (index === 1 && brand) {
        this.load({ type: type, brand: brand }, 'models', 2);
      }
    }

    go() {
      const params = new URLSearchParams();
      this.selects.forEach((select) => {
        if (select.value) params.set(select.dataset.param, select.value);
      });
      const qs = params.toString();
      // Finder section on this page: update in place, no reload.
      if (document.querySelector('.mw-pf[data-source]')) {
        window.history.pushState({}, '', window.location.pathname + (qs ? '?' + qs : ''));
        window.dispatchEvent(new CustomEvent('mw:finder-navigate', { detail: { scroll: true } }));
        return;
      }
      window.location = this.target + (qs ? '?' + qs : '');
    }

    toggleLoading(on) {
      this.classList.toggle('is-loading', on);
      this.button.classList.toggle('loading', on);
    }
  }

  customElements.define('batteriefinder-widget', BatteriefinderWidget);
}
